/*
 * Sketch to Slide — DeckorateAPI client.
 *
 * Talks to POST /v1/deckorate/image/sketch-to-slide. Full path used since
 * config.deckorateApiBaseUrl carries no /v1/deckorate segment — same
 * convention photoHeadApi.ts uses for /v1/deckorate/image/photo-head.
 * The endpoint downloads the referenced blob server-side and runs one vision
 * call that both classifies the sketch's layout and extracts its structure
 * into a versioned SlideSpec; it does not accept inline base64, so the
 * caller must have already placed the sketch at
 * `image_blob_container`/`image_blob_name` (see sketchToSlideUpload.ts)
 * before calling this.
 *
 * Field naming mirrors DeckorateAPI's Pydantic models exactly (snake_case
 * envelope: `request_id`, `deck_context`, `slide_spec`, `shape_type`, etc. —
 * see DeckorateAPI/models/sketch_to_slide_api/sketch_to_slide.py).
 */

import type { HttpClient, HttpRequestOptions } from "../../../services/deckorateApi";
import type { SlideDimensions, ThemeColors } from "../../create-slide/service/slideGenerationApi";
import {
  createConnectorLogger,
  getErrorMessage,
  isAbortError,
  parseHttpError,
  parseJsonErrorBody,
  HTTP_STATUS,
} from "../../shared/services/connectorErrors";

export { getErrorMessage, isAbortError } from "../../shared/services/connectorErrors";

const SKETCH_TO_SLIDE_CONNECTOR = "deckorate";
const SKETCH_TO_SLIDE_PATH = "/v1/deckorate/image/sketch-to-slide";

/** One vision call plus (server-side) up to one retry on unparseable JSON —
 *  mirrors Convert to Infographic's 90s budget for a comparable single AI pass. */
export const SKETCH_TO_SLIDE_REQUEST_TIMEOUT_MS = 90_000;

export const logSketchToSlideError = createConnectorLogger(SKETCH_TO_SLIDE_CONNECTOR, "SketchToSlideError");

/* ── Error copy — same createHttpClient(`Deckorate POST <path>: <status> <body>`)
 * parsing convention as photoHeadApi.ts's describePhotoHeadFailure(), plus a
 * by-`code` layer: DeckorateAPI's ApiError envelope
 * (`{"error":{code,message,details,request_id}}`) gives sketch-to-slide
 * failures a small set of specific, actionable codes worth their own copy. */

const UNAUTHORIZED_MESSAGE = "Your session has expired — sign in again and retry.";
const SERVER_MESSAGE = "The sketch-to-slide service is unavailable right now. Nothing was changed.";
const UPSTREAM_MESSAGE = "The sketch-to-slide service is busy right now — try again in a moment.";
export const TIMEOUT_MESSAGE = "Analyzing the sketch took too long. Nothing was changed — try again.";
export const CANCELLED_MESSAGE = "Converting the sketch was cancelled. Nothing was changed.";

/** Copy for DeckorateAPI's typed sketch-to-slide error codes (see
 *  DeckorateAPI/services/sketch_to_slide_api/{sketch_to_slide,image}.py). Falls
 *  through to the API's own `message` for any code not listed here. */
const MESSAGE_BY_CODE: Record<string, string> = {
  LOW_CONFIDENCE:
    "This sketch couldn't be read with enough confidence to build a slide — try a clearer photo with distinct shapes and legible text.",
  UNREADABLE_IMAGE:
    "Could not extract a usable structure from this sketch — try a clearer drawing with distinct shapes and labels.",
  BLOB_EMPTY: "The uploaded sketch came back empty — try again with a different image.",
  MISSING_IMAGE_BLOB: "Couldn't locate the uploaded sketch — try again.",
  MISSING_CONTAINER: "Couldn't locate the uploaded sketch — try again.",
};

interface ApiErrorBody {
  readonly code: string | null;
  readonly message: string | null;
}

function parseApiErrorBody(rawBody: string): ApiErrorBody | null {
  return parseJsonErrorBody(
    rawBody,
    (parsed) => {
      const envelope = parsed as { message?: unknown; error?: { code?: unknown; message?: unknown } };
      if (typeof envelope.message === "string" && envelope.message) return { code: null, message: envelope.message };
      const nested = envelope.error;
      if (typeof nested?.message === "string" && nested.message) {
        return { code: typeof nested.code === "string" ? nested.code : null, message: nested.message };
      }
      return null;
    },
    logSketchToSlideError,
    "sketch-to-slide-error-body-parse",
  );
}

export function describeSketchToSlideFailure(
  error: unknown,
  fallback: string,
  options: { timedOut?: boolean } = {},
): string {
  if (isAbortError(error)) return options.timedOut ? TIMEOUT_MESSAGE : CANCELLED_MESSAGE;

  const raw = getErrorMessage(error, fallback);
  const parsed = parseHttpError(raw);
  if (!parsed) return raw;

  const { status } = parsed;
  const apiError = parseApiErrorBody(parsed.body);

  if (status === HTTP_STATUS.unauthorized || status === HTTP_STATUS.forbidden) return UNAUTHORIZED_MESSAGE;
  const codedMessage = apiError?.code ? MESSAGE_BY_CODE[apiError.code] : undefined;
  if (codedMessage) return codedMessage;
  if (status >= HTTP_STATUS.badGateway) return UPSTREAM_MESSAGE;
  if (status >= HTTP_STATUS.serverError) return SERVER_MESSAGE;
  return apiError?.message ?? raw;
}

/** `request_id` is a non-secret correlation id the backend echoes back for
 *  request/response tracing. The API fills one in when absent (Pydantic's
 *  `default_factory=uuid4`), so on a host without `crypto.randomUUID` we
 *  simply omit it rather than inventing a weaker id. */
function newRequestId(): string | undefined {
  return globalThis.crypto?.randomUUID?.();
}

/* ── Request ─────────────────────────────────────────────────────────────── */

export interface SketchToSlideBlobRef {
  readonly blobContainer?: string;
  readonly blobName: string;
}

export interface SketchToSlideDeckContext {
  readonly themeColors?: ThemeColors | null;
  readonly slideDimensions?: SlideDimensions;
  /** Which slide the result will land on — pass-through/logging only on the
   *  backend; it does not influence generation. */
  readonly targetSlideIndex?: number | null;
}

interface RawDeckContextRequest {
  theme_colors?: ThemeColors | null;
  slide_dimensions?: SlideDimensions;
  target_slide_index?: number | null;
}

interface RawSketchToSlideRequest {
  request_id?: string;
  image_blob_container?: string;
  image_blob_name: string;
  deck_context?: RawDeckContextRequest;
  options?: { confidence_threshold?: number | null };
}

/* ── Response ────────────────────────────────────────────────────────────── */

export type SlideShapeRole = "title" | "body" | "label" | "container" | "image";
export type SlideConnectorStyle = "straight" | "elbow" | "curved";

export interface SlideGeometry {
  readonly xPct: number;
  readonly yPct: number;
  readonly widthPct: number;
  readonly heightPct: number;
}

export interface SlideShapeStyle {
  readonly fillColorToken: string | null;
  readonly lineColorToken: string | null;
  readonly fontToken: "heading" | "body" | null;
}

export interface SlideShape {
  readonly id: string;
  readonly role: SlideShapeRole;
  /** OOXML prstGeom hint, e.g. "rect", "roundRect", "flowChartProcess" — an
   *  open-ended string on the wire (no server-side enum), so treat any value
   *  the mapper doesn't recognise as "unspecified" rather than an error. */
  readonly shapeType: string | null;
  readonly geometry: SlideGeometry;
  readonly text: string | null;
  readonly style: SlideShapeStyle;
}

export interface SlideConnector {
  readonly id: string;
  readonly fromId: string;
  readonly toId: string;
  readonly label: string | null;
  readonly style: SlideConnectorStyle;
}

export interface SlideSpec {
  readonly title: string | null;
  readonly shapes: readonly SlideShape[];
  readonly connectors: readonly SlideConnector[];
}

export interface SketchToSlideResult {
  readonly requestId: string;
  readonly confidence: number;
  readonly layoutFamily: string;
  readonly slideSpec: SlideSpec;
}

interface RawSlideGeometry {
  x_pct?: unknown;
  y_pct?: unknown;
  width_pct?: unknown;
  height_pct?: unknown;
}

interface RawSlideShapeStyle {
  fill_color_token?: unknown;
  line_color_token?: unknown;
  font_token?: unknown;
}

interface RawSlideShape {
  id?: unknown;
  role?: unknown;
  shape_type?: unknown;
  geometry?: unknown;
  text?: unknown;
  style?: unknown;
}

interface RawSlideConnector {
  id?: unknown;
  from_id?: unknown;
  to_id?: unknown;
  label?: unknown;
  style?: unknown;
}

interface RawSlideSpec {
  title?: unknown;
  shapes?: unknown;
  connectors?: unknown;
}

/* Deliberately `unknown`-typed: this is wire data, validated before use. */
interface RawSketchToSlideResponse {
  request_id?: unknown;
  confidence?: unknown;
  layout_family?: unknown;
  slide_spec?: unknown;
}

const SHAPE_ROLES: readonly SlideShapeRole[] = ["title", "body", "label", "container", "image"];
const CONNECTOR_STYLES: readonly SlideConnectorStyle[] = ["straight", "elbow", "curved"];

function numberOr(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function toGeometry(raw: unknown): SlideGeometry {
  const g = (typeof raw === "object" && raw !== null ? raw : {}) as RawSlideGeometry;
  return {
    xPct: numberOr(g.x_pct, 0),
    yPct: numberOr(g.y_pct, 0),
    widthPct: numberOr(g.width_pct, 0),
    heightPct: numberOr(g.height_pct, 0),
  };
}

function toShapeStyle(raw: unknown): SlideShapeStyle {
  const s = (typeof raw === "object" && raw !== null ? raw : {}) as RawSlideShapeStyle;
  const fontToken = s.font_token === "heading" || s.font_token === "body" ? s.font_token : null;
  return {
    fillColorToken: typeof s.fill_color_token === "string" && s.fill_color_token ? s.fill_color_token : null,
    lineColorToken: typeof s.line_color_token === "string" && s.line_color_token ? s.line_color_token : null,
    fontToken,
  };
}

function toShapeRole(raw: unknown): SlideShapeRole {
  return typeof raw === "string" && (SHAPE_ROLES as readonly string[]).includes(raw)
    ? (raw as SlideShapeRole)
    : "container";
}

function toConnectorStyle(raw: unknown): SlideConnectorStyle {
  return typeof raw === "string" && (CONNECTOR_STYLES as readonly string[]).includes(raw)
    ? (raw as SlideConnectorStyle)
    : "straight";
}

function toShape(raw: unknown): SlideShape | null {
  if (typeof raw !== "object" || raw === null) return null;
  const s = raw as RawSlideShape;
  if (typeof s.id !== "string" || !s.id) return null;
  return {
    id: s.id,
    role: toShapeRole(s.role),
    shapeType: typeof s.shape_type === "string" && s.shape_type ? s.shape_type : null,
    geometry: toGeometry(s.geometry),
    text: typeof s.text === "string" && s.text ? s.text : null,
    style: toShapeStyle(s.style),
  };
}

function toConnector(raw: unknown, validShapeIds: ReadonlySet<string>): SlideConnector | null {
  if (typeof raw !== "object" || raw === null) return null;
  const c = raw as RawSlideConnector;
  if (typeof c.id !== "string" || !c.id) return null;
  if (typeof c.from_id !== "string" || typeof c.to_id !== "string") return null;
  // The backend already guarantees from_id/to_id reference real shapes
  // (SlideSpec's model validator) — this re-check is defence in depth against
  // a future schema drift, not duplicated business logic.
  if (!validShapeIds.has(c.from_id) || !validShapeIds.has(c.to_id)) return null;
  return {
    id: c.id,
    fromId: c.from_id,
    toId: c.to_id,
    label: typeof c.label === "string" && c.label ? c.label : null,
    style: toConnectorStyle(c.style),
  };
}

function toSlideSpec(raw: unknown): SlideSpec {
  const spec = (typeof raw === "object" && raw !== null ? raw : {}) as RawSlideSpec;
  const rawShapes = Array.isArray(spec.shapes) ? spec.shapes : [];
  const shapes = rawShapes.map(toShape).filter((s): s is SlideShape => s !== null);
  const shapeIds = new Set(shapes.map((s) => s.id));
  const rawConnectors = Array.isArray(spec.connectors) ? spec.connectors : [];
  const connectors = rawConnectors
    .map((c) => toConnector(c, shapeIds))
    .filter((c): c is SlideConnector => c !== null);
  return {
    title: typeof spec.title === "string" && spec.title ? spec.title : null,
    shapes,
    connectors,
  };
}

function normaliseResult(raw: RawSketchToSlideResponse | null | undefined, fallbackRequestId: string): SketchToSlideResult {
  const body = raw ?? {};
  const confidence = numberOr(body.confidence, 0);
  return {
    requestId: typeof body.request_id === "string" && body.request_id ? body.request_id : fallbackRequestId,
    confidence: Math.min(1, Math.max(0, confidence)),
    layoutFamily: typeof body.layout_family === "string" && body.layout_family ? body.layout_family : "freeform",
    slideSpec: toSlideSpec(body.slide_spec),
  };
}

/** POST the blob reference + deck context and return the parsed slide spec. */
export async function postSketchToSlide(
  http: HttpClient,
  ref: SketchToSlideBlobRef,
  deckContext: SketchToSlideDeckContext,
  options: HttpRequestOptions & { confidenceThreshold?: number | null } = {},
): Promise<SketchToSlideResult> {
  if (!ref.blobName) {
    throw new Error("No uploaded sketch to analyze — image_blob_name is missing.");
  }

  const requestId = newRequestId();
  const request: RawSketchToSlideRequest = {
    ...(requestId ? { request_id: requestId } : {}),
    image_blob_name: ref.blobName,
    ...(ref.blobContainer ? { image_blob_container: ref.blobContainer } : {}),
    deck_context: {
      ...(deckContext.themeColors ? { theme_colors: deckContext.themeColors } : {}),
      ...(deckContext.slideDimensions ? { slide_dimensions: deckContext.slideDimensions } : {}),
      ...(typeof deckContext.targetSlideIndex === "number" ? { target_slide_index: deckContext.targetSlideIndex } : {}),
    },
    ...(typeof options.confidenceThreshold === "number"
      ? { options: { confidence_threshold: options.confidenceThreshold } }
      : {}),
  };

  const requestOptions: HttpRequestOptions = options.signal ? { signal: options.signal } : {};
  try {
    const response = await http.post<RawSketchToSlideResponse | null>(SKETCH_TO_SLIDE_PATH, request, requestOptions);
    return normaliseResult(response, requestId ?? "");
  } catch (error) {
    if (!isAbortError(error)) logSketchToSlideError("sketch-to-slide-request-failed", getErrorMessage(error, ""));
    throw error;
  }
}
